/**
 * WB4 FC-06 — Downstream spawn metadata SSOT (P5-07 · operating-depth continuity).
 *
 * Central registry for auxiliary pipeline · drag-drop · post-action spawn anchors.
 * Consumed by workbench-auxiliary-pipeline and check:workbench-flow-continuity FC-06.
 */

export type AuxiliaryDownstreamSpawnKind = 'flow' | 'thread' | 'task' | 'queue' | 'link';

export interface AuxiliaryDownstreamSpawnEntry {
  actionId: string;
  spawnKind: AuxiliaryDownstreamSpawnKind;
  label: string;
  /** flowTemplateId · queue path · thread taskRef · production execute id */
  downstreamRef: string;
}

export const WORKBENCH_AUXILIARY_DOWNSTREAM_SPAWN_REGISTRY: readonly AuxiliaryDownstreamSpawnEntry[] =
  [
    {
      actionId: 'start_order_workflow',
      spawnKind: 'flow',
      label: '주문 intake',
      downstreamRef: 'order_intake',
    },
    {
      actionId: 'collection:open_follow_up_thread',
      spawnKind: 'thread',
      label: '수금 후속 Thread',
      downstreamRef: 'collection:open_follow_up_thread',
    },
    {
      actionId: 'drag_drop:route_to_picking_queue',
      spawnKind: 'queue',
      label: '피킹 큐',
      downstreamRef: 'warehouse/picking',
    },
    {
      actionId: 'drag_drop:route_to_collection_queue',
      spawnKind: 'queue',
      label: '수금 큐',
      downstreamRef: 'collection:queue',
    },
    {
      actionId: 'reference:attach_evidence',
      spawnKind: 'queue',
      label: '증빙 첨부',
      downstreamRef: 'approval_packet',
    },
    {
      actionId: 'drag_drop:attach_approval_packet',
      spawnKind: 'queue',
      label: '승인 패킷',
      downstreamRef: 'approval_packet',
    },
    {
      actionId: 'drag_drop:start_production_flow',
      spawnKind: 'flow',
      label: '생산 실행',
      downstreamRef: 'v2.production_order_execute',
    },
    {
      actionId: 'drag_drop:link_document_to_order',
      spawnKind: 'link',
      label: '문서-주문 연결',
      downstreamRef: 'link:document_order',
    },
    {
      actionId: 'drag_drop:link_order_to_customer',
      spawnKind: 'link',
      label: '주문-고객 연결',
      downstreamRef: 'link:order_customer',
    },
  ] as const;

export interface AuxiliaryPipelineSpawnMetadata {
  spawn: AuxiliaryDownstreamSpawnKind;
  actionId: string;
  downstreamRef: string;
  taskRef?: string;
}

export function resolveAuxiliaryDownstreamSpawnEntry(
  actionId: string
): AuxiliaryDownstreamSpawnEntry | undefined {
  return WORKBENCH_AUXILIARY_DOWNSTREAM_SPAWN_REGISTRY.find((entry) => entry.actionId === actionId);
}

/** P5-07 — downstream spawn metadata for auxiliary pipeline evidence/timeline chain. */
export function buildAuxiliaryPipelineSpawnMetadata(
  actionId: string
): AuxiliaryPipelineSpawnMetadata | undefined {
  const entry = resolveAuxiliaryDownstreamSpawnEntry(actionId);
  if (!entry) {
    return undefined;
  }

  if (entry.actionId === 'drag_drop:route_to_collection_queue') {
    return {
      spawn: entry.spawnKind,
      actionId: entry.actionId,
      downstreamRef: entry.downstreamRef,
      taskRef: `collection:queue`,
    };
  }

  return {
    spawn: entry.spawnKind,
    actionId: entry.actionId,
    downstreamRef: entry.downstreamRef,
  };
}
